'use client';

import { useState } from 'react';
import classNames from 'classnames';
import {
  ScoreOption,
  SCORES,
  SCORES_ALL,
} from '@/utils/api/reviews/reviews.data-model';
import { ibm } from '@/utils/fonts/fonts';

import RiSearchLine from '~icons/ri/search-line';
import RiArrowDownSLine from '~icons/ri/arrow-down-s-line';
import { Genre } from '@/utils/api/genres/genres.data-model';

interface GameSearchProps {
  onSearchScoreChanges: (score: string) => void;
  onSearchQChanges: (query: string) => void;
  onGenresChanges: (genres: number[]) => void;
  genres: Genre[];
}

export default function GameSearch({
  onSearchScoreChanges,
  onSearchQChanges,
  onGenresChanges,
  genres,
}: GameSearchProps) {
  const [isGenresPanelOpen, setGenresPanelOpen] = useState(false);
  const [selectedScore, setSelectedScore] = useState<ScoreOption>(
    SCORES_ALL[0]
  );
  const [selectedGenres, setSelectedGenres] = useState<number[]>([]);
  const [searchQ, setSearchQ] = useState('');

  const handleScoreSelect = (score: ScoreOption) => {
    const newScore =
      selectedScore.value === score.value ? SCORES_ALL[1] : score;
    setSelectedScore(newScore);
    onSearchScoreChanges(newScore.value);
  };

  const handleQueryChange = (q: string) => {
    setSearchQ(q);
    onSearchQChanges(q);
  };

  const handleGenreToggle = (genreId: number) => {
    setSelectedGenres(prev => {
      const newGenres = prev.includes(genreId)
        ? prev.filter(g => g !== genreId)
        : [...prev, genreId];
      onGenresChanges(newGenres);
      return newGenres;
    });
  };

  const handleGenresReset = () => {
    setSelectedGenres([]);
    onGenresChanges([]);
  };

  const groupGenresByCategory = (genres: Genre[]) => {
    const map = new Map<number, Genre[]>();

    for (const genre of genres) {
      const group = map.get(genre.category) ?? [];
      group.push(genre);
      map.set(genre.category, group);
    }

    return Array.from(map.entries()).map(([category, items]) => ({
      category,
      items,
    }));
  };

  return (
    <section style={ibm.style} className="flex flex-col items-end gap-3">
      <section className="flex w-full flex-row flex-wrap gap-3 text-sm">
        <div className="flex h-14 grow flex-row items-center gap-3 rounded-full bg-[#0B0B0B] px-6 py-3 md:h-16">
          <RiSearchLine className="ml-3" />
          <input
            placeholder="Название игры..."
            type="search"
            value={searchQ}
            onChange={e => handleQueryChange(e.target.value)}
            className="w-full border-none bg-transparent py-2 pr-3 outline-none placeholder:text-zinc-400"
          />
        </div>

        <section className="flex h-14 w-full flex-row justify-between gap-1 rounded-full bg-[#0B0B0B] px-4 py-3 text-base md:h-16 md:w-fit md:py-3">
          {SCORES.map(score => (
            <button
              key={score.value}
              onClick={() => handleScoreSelect(score)}
              title={score.label}
              className={classNames(
                'rounded-full px-5 transition-all duration-200 ease-in-out hover:bg-zinc-800',
                { 'bg-zinc-800': selectedScore.value === score.value }
              )}
            >
              {score.emoji}
            </button>
          ))}
        </section>

        <button
          onClick={() => setGenresPanelOpen(prev => !prev)}
          className="flex h-14 grow flex-row items-center justify-between gap-3 rounded-full bg-[#0B0B0B] px-6 py-3 transition-colors hover:bg-zinc-800 md:h-16 md:grow-0"
        >
          Жанры
          <RiArrowDownSLine
            className={classNames('transition-transform duration-200', {
              'rotate-180': isGenresPanelOpen,
            })}
          />
        </button>
      </section>

      {isGenresPanelOpen && (
        <section className="flex w-full flex-col gap-3 rounded-xl bg-[#0B0B0B] px-3 py-3">
          <section className="grid w-full grid-cols-2 justify-between gap-1 md:flex md:flex-row md:flex-wrap">
            {groupGenresByCategory(genres).map(({ category, items }) => (
              <div key={category} className="my-3 flex flex-col gap-1 md:my-0">
                <div className="flex w-fit flex-col items-start gap-1">
                  {items.map(genre => (
                    <button
                      key={genre.id}
                      onClick={() => handleGenreToggle(genre.id)}
                      className={classNames(
                        'rounded-full px-4 py-1 text-start text-base transition-all hover:bg-zinc-800',
                        { 'bg-zinc-800': selectedGenres.includes(genre.id) }
                      )}
                    >
                      {genre.title}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </section>
          <button
            onClick={handleGenresReset}
            className="m-3 rounded-full bg-[#151515]/80 py-2 text-sm transition-all hover:bg-zinc-800"
          >
            Сбросить фильтр жанров
          </button>
        </section>
      )}
    </section>
  );
}
